//To use this, put this in your HTML:
//  <designation-chooser #msgBox></designation-chooser>
//Then put this in your TS file:
//  @ViewChild('desChooser') desChooser: DesignationChooserComponent;
//Then initialize it by calling the init() function. 
//The data source for this control is an array of DesignationChoice objects. Each object will appear as a table row in the UI. Each row will have an amount, a dropdown of designations, and then other dropdowns of sub-designations that appear/disappear/fill depending on which designation is chosen.
import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { WebService, Designation, DesignationConfig, DesignationChoice, ApiResponse } from '../../shared/web-service/web.service';
import { Formatter, SessionSave } from '../../shared/utilities/utilities';

@Component({
  selector: 'designation-chooser',
  templateUrl: './designation-chooser.component.html',
  styleUrls: ['./designation-chooser.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class DesignationChooserComponent implements OnInit {
  desOptions: Designation[] = [];
  desChoices: DesignationChoice[] = []; //Main data source for this control. See comment at top of file.
  allowAddMoreRows: boolean;
  differenceBetweenCorrectSumAndTotalDes: string;
  totalCountElemID: string;
  contributionsPage: boolean;
  reportsPage: boolean;
  advancedFilter: boolean;
  negativeNumber: number = -1; //Kludge: I found an apparent bug in Angular where if you set the data source of a dropdown value to 0 (which should make the dropdown blank), it sometimes didn't work if the old value was already 0, in which case the dropdown would show the first item in the list instead of being blank, which was an error. So if you set the data source to a number which is not one of the items in the list, and is different from the old value, it works. So we use this to set the value to a different negative value. All the items in the list have a positive value, so this will make it blank.
  pledgeInfo: number[][];
  asterisk: string = " *"; //If this is not being used on the Reports page, the various dropdowns are required, so we add an asterisk to the labels. If this is on the reports page, then we just set this variable to a blank string.

  static desConfigHash = {};

  constructor(private webService: WebService) {}

  ngOnInit() {
  }

  //This will clear out our config hash so that we get them all from the server again. Call this if the configs have changed.
  static clearConfigHash() {
    DesignationChooserComponent.desConfigHash = {};
  }

  //Call this to initalize the control. Use the "edit" param to let us know if it's edit or add (pass false for add). Pass in "allowAddMoreRows" as true if the user has the ability to click a link to add more items to the data source array (which will then add a row to the UI). If this control can add multiple rows, then totalCountElemID identifies the field that holds the amount that all of the des amount rows must add up to. contributionsPage should be true if this is being used on the C pages. reportsPage should be true if this is being used on the reports pages. advancedFilter should be true if this is being used in the advanced filter control. Each of these has customized behavior.
  initDesChooser(edit: boolean, desOptions: Designation[], desChoices: DesignationChoice[], allowAddMoreRows: boolean, totalCountElemID: string, contributionsPage: boolean, reportsPage: boolean = false, advancedFilter: boolean = false) {
    this.pledgeInfo = null;
    this.desOptions = desOptions;
    this.desChoices = desChoices;
    this.contributionsPage = contributionsPage;
    this.reportsPage = reportsPage;
    this.advancedFilter = advancedFilter;
    this.totalCountElemID = totalCountElemID;
    this.allowAddMoreRows = allowAddMoreRows;
    this.differenceBetweenCorrectSumAndTotalDes = "0.00";
    if (reportsPage)
      this.asterisk = ""; //see comment above on the asterisk variable
    if (edit) {
      //initialize all the dropdown values for an existing des choice
      for (let i = 0; i < this.desChoices.length; i++)
        this.onDesignationSelected(i, true);
    }
    else { //Load the first des choice with a default value from session storage.
      SessionSave.get(SessionSave.defaultDesignationKey, desChoices[0]);
      desChoices[0].choiceAmount = null;
      desChoices[0].allowPledge = false;
    }
  }

  //This gives the pledge info for the currently selected donor. This will be called when, for example, a new donor has been chosen. The pledgeInfo is an array of int arrays. The first dimension of the array has one item for each pledge for the donor. The second dimension is an array with two numbers: the PledgeNo and DesConfigNo.
  setPledgeInfo(pledgeInfo: number[][], initializingEditDlg: boolean) {
    this.pledgeInfo = pledgeInfo;
    //When the pledgeInfo changes, we have to go through all the rows and make the "fulfills pledge" checkbox visible/invisible for each one, which is based somewhat on the pledgeInfo.
    if (this.desChoices) {
      for (let i = 0; i < this.desChoices.length; i++)
        this.checkForAllowPledge(i, initializingEditDlg);
    }
  }

  //Event handler for if a designation is selected from the dropdown. If initializingEditDlg is true, then we are initializing all the dropdown values for an existing setup rather than responding to a user click.
  onDesignationSelected(row: number, initializingEditDlg: boolean = false) {
    // each designation has an associated desConfig that we get from the server when that designation is chosen. So that we don't have to keep going back to the server if the same designation is chosen again, we save it in a hash table. So here we check if it's in the hash table. If it's not, we get it from the server.
    if (DesignationChooserComponent.desConfigHash[this.desChoices[row].desNo]) {
      this.desChoices[row].desDetails = DesignationChooserComponent.desConfigHash[this.desChoices[row].desNo];
      this.setUpDesConfig(row, initializingEditDlg);
    }
    else {
      this.webService.designations_GetDesignationDetails(this.desChoices[row].desNo).subscribe(
        apiResponse => {
          if (ApiResponse.checkStatus(apiResponse)) {
            this.desChoices[row].desDetails = apiResponse;
            this.setUpDesConfig(row, initializingEditDlg);
            DesignationChooserComponent.desConfigHash[this.desChoices[row].desNo] = apiResponse; //Save it in the hash table.
          }
        }
      );
    }
  }

  //This is called when a new designation is selected. It clears out the three sub-des lists and then fills in the first sub-des list (if needed). If initializingEditDlg is true, then we are initializing all the dropdown values for an existing setup rather than responding to a user click.
  setUpDesConfig(row: number, initializingEditDlg: boolean = false) {
    //Set desSelected to whatever des is selected in the des dropdown
    this.desChoices[row].desSelected = this.desOptions.find(x => x.desNo == this.desChoices[row].desNo);
    this.desChoices[row].subItems1.length = 0;
    this.desChoices[row].subItems2.length = 0;
    this.desChoices[row].subItems3.length = 0;
    this.desChoices[row].allowPledge = false;

    //one tricky thing is that this function is called not only when the user selects a new designation, but also when the edit dialog first appears, and we need to get the des details from the server and fill in all the dropdowns with its data. So if initializingEditDlg is true, we fill in all three sub-des lists.
    const configs: DesignationConfig[] = this.desChoices[row].desDetails.configList; //The list of configs for the selected des.
    let oldSubItemNo: number = null;
    //When this is used on the Reports page, its functionality is completely different. Instead of filling in the dropdowns one at a time, from left to right as a value is chosen from the next one, when a des is selected, all three dropdowns are filled in. This is because on the reports page, the user can select a value from a dropdown on the right without one on the left.
    if (!this.reportsPage) {
      if (this.desChoices[row].desSelected?.subName1) {
        for (let i = 0; i < configs.length; i++) {
          //this fills in the first sub-des list. We go through all the configs and add the corresponding subItems1 item to the list. Only add an item to the list if it has a config that is both active and pledgeable (but on the cont page, we show ones that are not pledgeable). For example, if there’s a D with the first sub-D level of year, and one of the options for year is 2019, but (on the D edit page) none of the rows in the bottom grid for 2019 are activated, then 2019 will not show up in the sub-D list. If initializingEditDlg is true, and an item matches the currently chosen value of the pledge's D, then we need to add the item to the list even if it's not active and pledgeable. This is for the case where there’s a pledge that is using a particular set of sub-D’s, then you go to the D page and deactivate that particular set of sub-D’s, then you go back to the pledge and edit it. The user can keep that set even though it’s no longer valid.
          if (configs[i].subItemNo1 !== oldSubItemNo &&
            ((initializingEditDlg && configs[i].subItemNo1 == this.desChoices[row].subItemNo1) || 
              ((configs[i].allowPledge || this.contributionsPage) && configs[i].configActive))) {
            oldSubItemNo = configs[i].subItemNo1;
            this.desChoices[row].subItems1.push(this.desChoices[row].desDetails.subItemList1.find(x => x.subItemNo === oldSubItemNo));
          }
        }
      }
    }
    else {
      //On the reports page, fill in all three sub-d dropdowns with all the possible values.
      for (let i = 0; i < this.desChoices[row].desDetails.subItemList1.length; i++)
        this.desChoices[row].subItems1.push(this.desChoices[row].desDetails.subItemList1[i]);
      for (let i = 0; i < this.desChoices[row].desDetails.subItemList2.length; i++)
        this.desChoices[row].subItems2.push(this.desChoices[row].desDetails.subItemList2[i]);
      for (let i = 0; i < this.desChoices[row].desDetails.subItemList3.length; i++)
        this.desChoices[row].subItems3.push(this.desChoices[row].desDetails.subItemList3[i]);
    }

    if (initializingEditDlg) {
      this.onSubItem1Selected(row, true);
      this.onSubItem2Selected(row, true);
    }
    else {
      this.desChoices[row].subItemNo1 = this.negativeNumber--; //see comment at top of file about negativeNumber
      this.desChoices[row].subItemNo2 = this.negativeNumber--;
      this.desChoices[row].subItemNo3 = this.negativeNumber--;
    }
    this.checkForAllowPledge(row, initializingEditDlg);
  }
  
  //Event handler for if an item is selected in the sub-des 1 dropdown on the add/edit dialog. If initializingEditDlg is true, then we are initializing all the dropdown values for an existing setup rather than responding to a user click.
  onSubItem1Selected(row: number, initializingEditDlg: boolean = false) {
    //See comment in setUpDesConfig() about how if this is being used on the Reports page, the functionality is completely different.
    if (this.reportsPage)
      return;

    //clear out the two last sub-des lists and then fill in the second sub-des list.
    this.desChoices[row].subItems2.length = 0;
    this.desChoices[row].subItems3.length = 0;
    if (!initializingEditDlg) {
      this.desChoices[row].subItemNo2 = this.negativeNumber--; //see comment at top of file about negativeNumber
      this.desChoices[row].subItemNo3 = this.negativeNumber--;
    }
    const configs: DesignationConfig[] = this.desChoices[row].desDetails.configList;
    let oldSubItemNo: number = null;
    for (let i = 0; i < configs.length; i++) {
      //this fills in the second sub-des list. We go through all the configs and add the corresponding subItems2 item to the list. Only add an item to the list if it has a config that is both active and pledgeable (but on the cont page, we show ones that are not pledgeable). For example, if there’s a D with the first sub-D level of year, and one of the options for year is 2019, but (on the D edit page) none of the rows in the bottom grid for 2019 are activated, then 2019 will not show up in the sub-D list. If initializingEditDlg is true, and an item matches the currently chosen value of the pledge's D, then we need to add the item to the list even if it's not active and pledgeable. This is for the case where there’s a pledge that is using a particular set of sub-D’s, then you go to the D page and deactivate that particular set of sub-D’s, then you go back to the pledge and edit it. The user can keep that set even though it’s no longer valid.
      if (configs[i].subItemNo1 == this.desChoices[row].subItemNo1 && configs[i].subItemNo2 !== oldSubItemNo &&
        ((initializingEditDlg && configs[i].subItemNo2 == this.desChoices[row].subItemNo2) || 
          ((configs[i].allowPledge || this.contributionsPage) && configs[i].configActive))) {
        oldSubItemNo = configs[i].subItemNo2;
        this.desChoices[row].subItems2.push(this.desChoices[row].desDetails.subItemList2.find(x => x.subItemNo === oldSubItemNo));
      }
    }
    this.checkForAllowPledge(row, initializingEditDlg);
  }

  //Event handler for if an item is selected in the sub-des 2 dropdown on the add/edit dialog. If initializingEditDlg is true, then we are initializing all the dropdown values for an existing setup rather than responding to a user click.
  onSubItem2Selected(row: number, initializingEditDlg: boolean = false) {
    //See comment in setUpDesConfig() about how if this is being used on the Reports page, the functionality is completely different.
    if (this.reportsPage)
      return;

    //clear out the last sub-des list and then fill it in.
    this.desChoices[row].subItems3.length = 0;
    if (!initializingEditDlg) {
      this.desChoices[row].subItemNo3 = this.negativeNumber--; //see comment at top of file about negativeNumber
    }
    const configs: DesignationConfig[] = this.desChoices[row].desDetails.configList;
    let oldSubItemNo: number = null;
    for (let i = 0; i < configs.length; i++) {
      //this fills in the third sub-des list. We go through all the configs and add the corresponding subItems2 item to the list. Only add an item to the list if it has a config that is both active and pledgeable (but on the cont page, we show ones that are not pledgeable). For example, if there’s a D with the first sub-D level of year, and one of the options for year is 2019, but (on the D edit page) none of the rows in the bottom grid for 2019 are activated, then 2019 will not show up in the sub-D list. If initializingEditDlg is true, and an item matches the currently chosen value of the pledge's D, then we need to add the item to the list even if it's not active and pledgeable. This is for the case where there’s a pledge that is using a particular set of sub-D’s, then you go to the D page and deactivate that particular set of sub-D’s, then you go back to the pledge and edit it. The user can keep that set even though it’s no longer valid.
      if (configs[i].subItemNo1 == this.desChoices[row].subItemNo1 && configs[i].subItemNo2 == this.desChoices[row].subItemNo2 && configs[i].subItemNo3 !== oldSubItemNo &&
        ((initializingEditDlg && configs[i].subItemNo3 == this.desChoices[row].subItemNo3) || 
          ((configs[i].allowPledge || this.contributionsPage) && configs[i].configActive))) {
        oldSubItemNo = configs[i].subItemNo3;
        this.desChoices[row].subItems3.push(this.desChoices[row].desDetails.subItemList3.find(x => x.subItemNo === oldSubItemNo));
      }
    }
    this.checkForAllowPledge(row, initializingEditDlg);
  }

  //Check to see if the row allows pledge based on the sub-Ds chosen. We use the values chosen to identify the D config. If there's a matching config, then check to see if it allows pledges and if the donor has a pledge for that config. If it does, then we show the "fulfills pledge" checkbox for the row.
  checkForAllowPledge(row: number, initializingEditDlg: boolean = false) {
    //if this is being used on the Reports page, there's no concept of allowing pledges
    if (this.reportsPage)
      return;

    if (this.desChoices[row].desDetails && this.desChoices[row].desDetails.configList) {
      let dc: DesignationConfig = this.desChoices[row].desDetails.configList.find(x =>
        (!this.desChoices[row].desSelected.subName1 || x.subItemNo1 == this.desChoices[row].subItemNo1) &&
        (!this.desChoices[row].desSelected.subName2 || x.subItemNo2 == this.desChoices[row].subItemNo2) &&
        (!this.desChoices[row].desSelected.subName3 || x.subItemNo3 == this.desChoices[row].subItemNo3));
      //desChoices[row].allowPledge is whether or not we display the checkbox for the row. The checkbox allows the user to specify if the sub-D fulfills a pledge. We only show the checkbox if all the sub-D levels have a value chosen, the des config allows pledges, and the current donor has a pledge for that des config. The other thing that can set this to true is if initializingEditDlg is true and fulfillsPledge is true. The reason for this is the scenario where you are editing/viewing an existing C where the D was marked as fulfills pledge in the past, but the pledge has been inactivated since that time, and we want to still show the D as fulfilling the pledge. In this scenario, the pledge won't be in the pledgeInfo array, but we still want to display the checkbox.
      //desChoices[row].fulfillsPledge is whether or not the checkbox is checked. We set it to true by default if the checkbox is visible and this is not being called during edit init.
      const allowsPledge: boolean = (dc && this.pledgeInfo && this.pledgeInfo.find(x => x[1] == dc.desConfigNo) != null) || (initializingEditDlg && this.desChoices[row].fulfillsPledge); //Check pledgeInfo to see if the donor has a pledge for this desConfigNo
      this.desChoices[row].allowPledge = allowsPledge;
      if (!initializingEditDlg)
        this.desChoices[row].fulfillsPledge = allowsPledge;
    }
  }

  //Adds a designation choice.
  addDesChoice(newAmount: string = null) {
    //This kludge is because there is a bug in angular. We should be able to do the one line of code right below that is commented out. But I found that in the UI it put the new row on the top rather than the bottom even though it was the last item in the array. This little delay code made it behave properly.
    //this.desChoices.push(new DesignationChoice());
    let temp: DesignationChoice[] = this.desChoices;
    this.desChoices = [];
    let newDesChoice = new DesignationChoice();
    if (newAmount)
      newDesChoice.choiceAmount = newAmount;
    temp.push(newDesChoice);
    setTimeout(() => { this.desChoices = temp }, 0);
  }

  //Deletes a designation choice.
  deleteChoice(i) {
    this.desChoices.splice(i, 1);
    //This will cause the "Remaining contribution to allocate" number at the bottom to get updated
    this.onBlurDesAmount(-1);
  }

  //returns true if there are no designations in the list that have the same combination of sub-Ds; false if there are duplicates.
  validateNoDuplicates() {
    let arr: Array<string> = [];
    let rval: boolean = true;
    //We go through every des choice and create a string that uniquely identifies the sub-Ds in it. We put that string in an array. For each subsequent choice, we check to see if there's a matching string already in the array.
    this.desChoices.forEach(choice => {
      let str: string = `$${choice.desSelected.subName1 ? choice.subItemNo1 : null},${choice.desSelected.subName2 ? choice.subItemNo2 : null},${choice.desSelected.subName3 ? choice.subItemNo3: null}`;
      if (arr.indexOf(str) === -1)
        arr.push(str);
      else
        rval = false;
    });
    return rval;
  }

  //If this control allows multiple rows, when the focus leaves one of the amount fields, see if all the amount fields adds up to the correct total amount, which can be found in a field outside of this control identified by totalCountElemID.
  //Pass in -1 for the rowIndex if this isn't actually being called from bluring one of the amount fields in the table, but you just want to recalc the remaining des amount.
  onBlurDesAmount(rowIndex: number) {
    if (rowIndex != -1)
      this.desChoices[rowIndex].choiceAmount = Formatter.toCurrency(this.desChoices[rowIndex].choiceAmount);
    if (!this.totalCountElemID || !this.allowAddMoreRows)
      return;

    //Put this logic inside brief delay because it sometimes wasn't getting the right numbers.
    setTimeout(() => {
      let correctTotalSum: number = Formatter.toNumber($("#" + this.totalCountElemID).val().toString());

      if (!isNaN(correctTotalSum) && correctTotalSum > 0) {
        let totalDes: number = 0; //this will receive all the des amounts summed together.
        this.desChoices.forEach(choice => {
          if (!isNaN(Formatter.toNumber(choice.choiceAmount)))
            totalDes += Formatter.toNumber(choice.choiceAmount);
        });
        //Updating this variable will display the amount needed to get to the correct total. 
        this.differenceBetweenCorrectSumAndTotalDes = Formatter.toCurrency(correctTotalSum - totalDes);
        //If the field triggering this is in the last row, and the total of the des amounts is less than the needed total, and there's a number in the last row amount, then add another row with the amount needed to get to the correct total.
        if (rowIndex == this.desChoices.length - 1 && correctTotalSum - totalDes > 0.000001 && this.desChoices.length > 0 && Formatter.toNumber(this.desChoices[this.desChoices.length - 1].choiceAmount) > 0) {
          this.addDesChoice(this.differenceBetweenCorrectSumAndTotalDes);
          this.differenceBetweenCorrectSumAndTotalDes = "0.00";
          //This will put the focus on the amount box in the newly created row and scroll down the div to the bottom. If you don't do this, the table with rows is recreated by angular, and scrolls to the top and loses focus.
          setTimeout(() => { $("#amount" + (rowIndex + 1).toString()).focus(); let objDiv = document.getElementById("dialogDiv"); objDiv.scrollTop = objDiv.scrollHeight; }, 100);
        }
      }
    }, 10);
  }
}
